import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ThermometerSun,
  LogOut,
  Users,
  Inbox,
  Loader2,
  CheckCircle2,
  Search,
  Phone,
  Mail,
  MapPin,
  Download,
  X,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUSES = ["Neu", "In Bearbeitung", "Abgeschlossen"];

const statusStyle = {
  Neu: "bg-terracotta/10 text-terracotta border-terracotta/20",
  "In Bearbeitung": "bg-amber-100 text-amber-700 border-amber-200",
  Abgeschlossen: "bg-brand/10 text-brand border-brand/20",
};

const sourceStyle = {
  Instagram: "bg-pink/10 text-pink border-pink/20",
  Facebook: "bg-sky/10 text-sky border-sky/20",
  "Meta Lead Ad": "bg-sky/10 text-sky border-sky/20",
  Website: "bg-navy/5 text-navy border-navy/10",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, in_progress: 0, done: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const [l, s] = await Promise.all([api.get("/leads"), api.get("/leads/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } catch {
      toast.error("Leads konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      const s = await api.get("/leads/stats");
      setStats(s.data);
      toast.success("Status aktualisiert.");
    } catch {
      toast.error("Aktualisierung fehlgeschlagen.");
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const filtered = leads.filter((l) => {
    const q = query.toLowerCase();
    return (
      `${l.first_name} ${l.last_name}`.toLowerCase().includes(q) ||
      l.email.toLowerCase().includes(q) ||
      (l.city || "").toLowerCase().includes(q) ||
      (l.postal_code || "").includes(q)
    );
  });

  const cards = [
    { label: "Gesamt", value: stats.total, icon: Users, color: "text-forest" },
    { label: "Neu", value: stats.new, icon: Inbox, color: "text-terracotta" },
    { label: "In Bearbeitung", value: stats.in_progress, icon: Loader2, color: "text-amber-600" },
    { label: "Abgeschlossen", value: stats.done, icon: CheckCircle2, color: "text-brand" },
  ];

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("Keine Leads zum Exportieren.");
      return;
    }
    const headers = [
      "Name", "Quelle", "E-Mail", "Telefon", "Adresse", "PLZ", "Ort",
      "Gebäude", "Heizung", "Wohnfläche", "Eigentum", "Zeitrahmen",
      "Nachricht", "Status", "Eingang",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filtered.map((l) => [
      `${l.first_name} ${l.last_name}`.trim(),
      l.source || "Website",
      l.email, l.phone, l.address, l.postal_code, l.city,
      l.building_type, l.current_heating, l.living_area, l.ownership, l.timeframe,
      l.message, l.status,
      l.created_at ? new Date(l.created_at).toLocaleString("de-DE") : "",
    ].map(esc).join(";"));
    const csv = "\uFEFF" + [headers.map(esc).join(";"), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `waermekraft-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${filtered.length} Leads exportiert.`);
  };

  return (
    <div className="min-h-screen bg-bone flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-forest text-white p-6 shrink-0">
        <div className="flex items-center gap-2 mb-10">
          <span className="font-display font-extrabold text-xl text-white">Wärme<span className="text-pink">kraft</span></span>
        </div>
        <nav className="space-y-1 flex-1">
          <div className="flex items-center gap-3 rounded-lg bg-white/10 px-4 py-3 text-sm font-semibold">
            <Inbox className="h-4 w-4" /> Leads
          </div>
        </nav>
        <div className="border-t border-white/10 pt-4">
          <p className="text-xs text-white/40">Angemeldet als</p>
          <p className="text-sm font-semibold truncate">{user?.email}</p>
          <button onClick={doLogout} data-testid="logout-btn" className="mt-3 flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
            <LogOut className="h-4 w-4" /> Abmelden
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-[#D9DBE0] px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="font-display font-bold text-xl text-forest">Lead-Übersicht</h1>
            <p className="text-sm text-[#9AA0AC]">Alle eingehenden Wärmepumpen-Anfragen</p>
          </div>
          <button onClick={doLogout} data-testid="logout-btn-mobile" className="lg:hidden flex items-center gap-2 text-sm font-semibold text-forest">
            <LogOut className="h-4 w-4" /> Abmelden
          </button>
        </header>

        <div className="p-6 space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {cards.map((c, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#D9DBE0] p-5" data-testid={`stat-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#9AA0AC]">{c.label}</span>
                  <c.icon className={`h-5 w-5 ${c.color}`} />
                </div>
                <p className="mt-2 font-display font-bold text-3xl text-forest">{c.value}</p>
              </div>
            ))}
          </div>

          {/* Search + Export */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9AA0AC]" />
              <Input
                data-testid="lead-search"
                placeholder="Suchen nach Name, E-Mail, PLZ..."
                className="pl-9 bg-white"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <button
              onClick={exportCsv}
              data-testid="export-csv-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-forest px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand transition-colors shrink-0"
            >
              <Download className="h-4 w-4" /> CSV exportieren
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-[#D9DBE0] overflow-hidden">
            {loading ? (
              <div className="py-20 flex justify-center">
                <Loader2 className="h-7 w-7 animate-spin text-brand" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-20 text-center text-[#9AA0AC]" data-testid="empty-leads">
                <Inbox className="h-10 w-10 mx-auto mb-3 opacity-40" />
                Noch keine Leads vorhanden.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="leads-table">
                  <thead>
                    <tr className="border-b border-[#D9DBE0] text-left text-[#9AA0AC] whitespace-nowrap">
                      <th className="px-5 py-3 font-semibold">Name</th>
                      <th className="px-5 py-3 font-semibold">Quelle</th>
                      <th className="px-5 py-3 font-semibold">E-Mail</th>
                      <th className="px-5 py-3 font-semibold">Telefon</th>
                      <th className="px-5 py-3 font-semibold">Adresse</th>
                      <th className="px-5 py-3 font-semibold">Ort</th>
                      <th className="px-5 py-3 font-semibold">Gebäude</th>
                      <th className="px-5 py-3 font-semibold">Heizung</th>
                      <th className="px-5 py-3 font-semibold">Wohnfläche</th>
                      <th className="px-5 py-3 font-semibold">Eigentum</th>
                      <th className="px-5 py-3 font-semibold">Zeitrahmen</th>
                      <th className="px-5 py-3 font-semibold">Nachricht</th>
                      <th className="px-5 py-3 font-semibold">Eingang</th>
                      <th className="px-5 py-3 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l) => (
                      <tr
                        key={l.id}
                        className="border-b border-[#E4E5E9] hover:bg-bone cursor-pointer transition-colors"
                        onClick={() => setSelected(l)}
                        data-testid={`lead-row-${l.id}`}
                      >
                        <td className="px-5 py-4 font-semibold text-forest whitespace-nowrap">{l.first_name} {l.last_name}</td>
                        <td className="px-5 py-4">
                          <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full border ${sourceStyle[l.source] || sourceStyle.Website}`} data-testid={`lead-source-${l.id}`}>
                            {l.source || "Website"}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-[#5A6172] whitespace-nowrap">{l.email || "—"}</td>
                        <td className="px-5 py-4 text-[#5A6172] whitespace-nowrap">{l.phone || "—"}</td>
                        <td className="px-5 py-4 text-[#5A6172] whitespace-nowrap">{l.address || "—"}</td>
                        <td className="px-5 py-4 text-[#5A6172] whitespace-nowrap">{`${l.postal_code || ""} ${l.city || ""}`.trim() || "—"}</td>
                        <td className="px-5 py-4 text-[#5A6172] whitespace-nowrap">{l.building_type || "—"}</td>
                        <td className="px-5 py-4 text-[#5A6172] whitespace-nowrap">{l.current_heating || "—"}</td>
                        <td className="px-5 py-4 text-[#5A6172] whitespace-nowrap">{l.living_area || "—"}</td>
                        <td className="px-5 py-4 text-[#5A6172] whitespace-nowrap">{l.ownership || "—"}</td>
                        <td className="px-5 py-4 text-[#5A6172] whitespace-nowrap">{l.timeframe || "—"}</td>
                        <td className="px-5 py-4 text-[#5A6172] max-w-[220px] truncate" title={l.message || ""}>{l.message || "—"}</td>
                        <td className="px-5 py-4 text-[#9AA0AC] whitespace-nowrap">
                          {l.created_at ? (
                            <>
                              <div>{new Date(l.created_at).toLocaleDateString("de-DE")}</div>
                              <div className="text-xs">{new Date(l.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr</div>
                            </>
                          ) : "—"}
                        </td>
                        <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                          <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                            <SelectTrigger
                              data-testid={`status-select-${l.id}`}
                              className={`h-8 w-[150px] text-xs font-semibold border ${statusStyle[l.status] || ""}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-detail">
          <div className="absolute inset-0 bg-forest/40 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-white h-full overflow-y-auto p-8 shadow-2xl">
            <button onClick={() => setSelected(null)} data-testid="detail-close" className="absolute top-5 right-5 h-8 w-8 rounded-full hover:bg-bone flex items-center justify-center">
              <X className="h-5 w-5 text-[#9AA0AC]" />
            </button>
            <div className="flex items-center gap-2">
              <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full border ${statusStyle[selected.status]}`}>{selected.status}</span>
              <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full border ${sourceStyle[selected.source] || sourceStyle.Website}`}>{selected.source || "Website"}</span>
            </div>
            <h2 className="mt-4 font-display font-bold text-2xl text-forest">{selected.first_name} {selected.last_name}</h2>
            <div className="mt-6 space-y-3 text-sm">
              <Row icon={Mail} value={selected.email} />
              <Row icon={Phone} value={selected.phone} />
              <Row icon={MapPin} value={`${selected.address ? selected.address + ", " : ""}${selected.postal_code} ${selected.city}`} />
            </div>
            <div className="mt-6 border-t border-[#D9DBE0] pt-6 grid grid-cols-2 gap-4 text-sm">
              <Detail label="Gebäudetyp" value={selected.building_type} />
              <Detail label="Akt. Heizung" value={selected.current_heating} />
              <Detail label="Wohnfläche" value={selected.living_area} />
              <Detail label="Eigentum" value={selected.ownership} />
              <Detail label="Zeitrahmen" value={selected.timeframe} />
            </div>
            {selected.message && (
              <div className="mt-6 border-t border-[#D9DBE0] pt-6">
                <p className="text-xs font-bold uppercase tracking-wider text-[#9AA0AC]">Nachricht</p>
                <p className="mt-2 text-sm text-[#5A6172] leading-relaxed">{selected.message}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-brand shrink-0" />
      <span className="text-forest">{value}</span>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-[#9AA0AC]">{label}</p>
      <p className="mt-1 text-forest font-medium">{value || "—"}</p>
    </div>
  );
}
